import React from "react";
import { injectIntl } from "react-intl";
import { withRouter } from "react-router-dom";

import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import METHODS from "../../helpers/methods";
import AudienceFilterLabel from "../AudienceFilterLabel/AudienceFilterLabel";
import getGenders from "../AudiencesSearch/components/filters/utils/getGenders";
import getSocialProviders from "../AudiencesSearch/components/filters/utils/getSocialProviders";
import TooltipPortal from "src/app/components/TooltipPortal/TooltipPortal";

export const CONTENT_KEYWORD_BLACKLIST = [
  "chosenKeywords",
  "searchHashtags",
  "searchMentions",
  "searchBioKeywords",
];

const tagNames = [
  {
    name: "audienceAge",
    id: "front_audience_age",
    defaultMessage: "Audience age",
    subfields: ["audienceAgeFrom", "audienceAgeTo", "audienceAgeDensity"],
    dash: true,
  },
  {
    name: "selectedAudienceCountries",
    id: "front_audience_country",
    defaultMessage: "Audience country",
    field: "label",
    secondField: "percent",
  },
  {
    name: "selectedAudienceLanguages",
    id: "front_audience_language",
    defaultMessage: "Audience language",
    field: "label",
    secondField: "percent",
  },
  {
    name: "audienceGender",
    id: "front_audience_gender",
    defaultMessage: "Audience gender",
    subfields: ["audienceGender", "audienceGenderDensity"],
  },
  {
    name: "chosenBrands",
    id: "front_audience_brand_affinity",
    defaultMessage: "Audience brand affinity",
  },
  {
    name: "chosenTopics",
    id: "front_audience_interests",
    defaultMessage: "Audience interests",
  },
  {
    name: "chosenInfluencerIgCategories",
    id: "front_influencer_ig_categories",
    defaultMessage: "Influencer categories",
  },
  {
    name: "chosenInfluencerSnapchatCategories",
    id: "front_influencer_snapchat_categories",
    defaultMessage: "Influencer categories",
  },
  {
    name: "chosenInfluencerInterests",
    id: "front_influencer_interests",
    defaultMessage: "Influencer interests",
  },
  {
    name: "influencerAge",
    id: "front_influencer_age",
    defaultMessage: "Influencer age",
    subfields: ["influencerAgeFrom", "influencerAgeTo"],
    dash: true,
  },
  {
    name: "selectedInfluencerCountries",
    id: "front_influencer_country",
    defaultMessage: "Influencer country",
    field: "label",
  },
  {
    name: "selectedInfluencerCities",
    id: "front_influencer_city",
    defaultMessage: "Influencer city",
    field: "label",
  },
  {
    name: "influencerFollowers",
    id: "front_followers_count",
    defaultMessage: "Followers count",
  },
  {
    name: "influencerSocialProvider",
    id: "front_influencer_social_platform",
    defaultMessage: "Social platform",
    field: "label",
  },
  {
    name: "selectedProviders",
    id: "front_influencer_social_platform",
    defaultMessage: "Social platform",
    field: "label",
  },
  {
    name: "influencerSocialUsername",
    id: "front_influencer_social_username",
    defaultMessage: "Username",
    field: "label",
  },
  {
    name: "influencerGender",
    id: "front_influencer_gender",
    defaultMessage: "Influencer gender",
    field: "label",
  },
  {
    name: "audienceCredibilityScore",
    id: "front_profile_data_credibility",
    defaultMessage: "Credibility",
    field: "label",
  },
  {
    name: "paidPostPerformanceMin",
    id: "front_profile_data_paid_post_performance",
    defaultMessage: "Paid post performance",
    field: "label",
  },
  {
    name: "minimumEr",
    id: "front_minimum_er",
    defaultMessage: "Minimum ER",
    field: "label",
  },
  {
    name: "chosenKeywords",
    id: "front_keyword",
    defaultMessage: "Keyword",
  },
  {
    name: "lastPostsWithin",
    id: "front_last_posts_within",
    defaultMessage: "Last posts within",
    field: "label",
  },
  {
    name: "searchContents",
    id: "front_content_keyword",
    defaultMessage: "Content keyword",
  },
  {
    name: "searchHashtags",
    id: "front_hashtag",
    defaultMessage: "Hashtag",
    option: "hashtagsOption",
  },
  {
    name: "searchMentions",
    id: "front_mention",
    defaultMessage: "Mention",
    option: "mentionsOption",
  },
  {
    name: "searchBioKeywords",
    id: "front_search_in_bio",
    defaultMessage: "Search in Bio",
  },
  {
    name: "hasContactData",
    id: "front_show_only_results_with_contact_data",
    defaultMessage: "Show only results with contact data",
    field: "label",
  },
  {
    name: "excludeCreatorsFromList",
    id: "front_exclude_creators_already_added_to_the_list",
    defaultMessage: "Exclude creators already added to the list",
    field: "label",
  },
  {
    name: "showOnlyCreatorsAvailableInCrm",
    id: "front_show_only_creators_available_in_crm",
    defaultMessage: "Show only creators available in CRM",
    field: "label",
  },
  {
    name: "chosenUnifiedCategories",
    id: "front_unified_categories",
    defaultMessage: "Influencer categories",
  },
];

function SearchLabels(props) {
  const {
    intl,
    afterRemoveCallback,
    searchFilters,
    closeFilters,
    changeFilters,
    interests,
    isCreatorSearch = false,
  } = props;

  const genders = getGenders(intl);
  const socialProviders = getSocialProviders(intl);

  const removeFilter = async (type, tagProps) => {
    if (closeFilters) {
      closeFilters();
    }
    const newFilters = { ...searchFilters };
    const { name, subfields, idx } = tagProps;

    if (name === "searchContents") {
      CONTENT_KEYWORD_BLACKLIST.forEach((keyword) => {
        const array =
          newFilters[keyword]?.filter(
            (i) => i !== searchFilters[keyword][idx],
          ) ?? [];
        newFilters[keyword] = array;
      });
    }

    if (type === "social-username") {
      const { username } = tagProps;
      newFilters[name] = (newFilters[name] || []).filter((u) => u !== username);
    } else if (type === "array") {
      const array = newFilters[name]?.filter(
        (i) => i !== searchFilters[name][idx],
      );
      newFilters[name] = array;
    } else if (type === "many-fields") {
      newFilters[subfields[0]] = null;
      newFilters[subfields[1]] = null;

      if (newFilters[subfields[2]]) {
        newFilters[subfields[2]] = null;
      }
    } else {
      newFilters[name] = null;
    }

    await changeFilters(newFilters);

    if (afterRemoveCallback) {
      afterRemoveCallback(tagProps.name);
    }
  };

  const createArrayFilterLabel = (tag, subTag, idx) => {
    const {
      name,
      id,
      defaultMessage,
      field,
      secondField: arraySecondField,
    } = tag;

    let content;
    if (subTag[field]) {
      content = subTag[field];
    } else if (name === "chosenTopics") {
      const interestObj = METHODS.findObjBySubTag(subTag, interests);
      content = (
        <IDHFormattedMessage
          id={interestObj?.name || subTag}
          defaultMessage={interestObj?.value || subTag}
        />
      );
    } else if (name === "selectedProviders") {
      const foundSocialProvider = socialProviders.find(
        (provider) => provider.value === subTag,
      );
      if (foundSocialProvider) {
        content = foundSocialProvider.label;
      } else {
        content = subTag;
      }
    } else {
      content = subTag;
    }

    return (
      <AudienceFilterLabel
        key={subTag[field] ? subTag[field] : subTag}
        id={id}
        defaultMessage={defaultMessage}
        removeFilter={() => removeFilter("array", { ...tag, idx })}
      >
        {content}
        {subTag[arraySecondField] && (
          <>
            &nbsp;&nbsp;
            {subTag[arraySecondField]}
            &nbsp;%
          </>
        )}
      </AudienceFilterLabel>
    );
  };

  const generateLabels = () => {
    let socialProviderIndex = 0;

    const filteredTagNames = tagNames.filter(
      (tag) =>
        !(
          searchFilters.searchContents?.length &&
          CONTENT_KEYWORD_BLACKLIST.includes(tag.name)
        ) && !(isCreatorSearch && tag.name === "influencerSocialProvider"),
    );

    const createInfluencerFollowersLabel = (tag, subTag, idx) => {
      const { id, defaultMessage } = tag;
      let rangeText;

      if (subTag.min && subTag.max) {
        rangeText = `${METHODS.splitNumbers(subTag.min)} - ${METHODS.splitNumbers(subTag.max)}`;
      } else {
        const minText =
          subTag.min !== null ? METHODS.splitNumbers(subTag.min) : "";
        const maxText =
          subTag.max !== null ? METHODS.splitNumbers(subTag.max) : "";
        rangeText = `${minText} > ${maxText}`;
      }

      return (
        <AudienceFilterLabel
          id={id}
          key={`${id}_${idx}`}
          defaultMessage={defaultMessage}
          removeFilter={() => removeFilter("array", { ...tag, idx })}
        >
          {rangeText}
        </AudienceFilterLabel>
      );
    };

    const createSubfieldsLabel = (tag, index) => {
      const { name, id, defaultMessage, subfields, dash } = tag;

      let firstFieldContent;
      if (name === "audienceGender") {
        firstFieldContent = genders.filter(
          (item) => item.value === searchFilters[subfields[0]].value,
        )[0].label;
      } else if (name === "influencerAge") {
        firstFieldContent = searchFilters[subfields[0]]?.value;
      } else {
        firstFieldContent = searchFilters[subfields[0]]?.label;
      }

      let separator;
      if (!dash) {
        separator = " ";
      } else {
        const hasFirstField = searchFilters[subfields[0]];
        const hasSecondField = searchFilters[subfields[1]];

        if (
          (hasFirstField && !hasSecondField) ||
          (!hasFirstField && hasSecondField)
        ) {
          separator = "<";
        } else {
          separator = "-";
        }
      }

      let secondFieldContent;
      if (name === "audienceGender") {
        secondFieldContent = `${searchFilters[subfields[1]]?.value} %`;
      } else if (name === "influencerAge") {
        secondFieldContent = searchFilters[subfields[1]]?.value;
      } else {
        secondFieldContent = searchFilters[subfields[1]]?.label;
      }

      return (
        <AudienceFilterLabel
          key={`${id}_${index}`}
          id={id}
          defaultMessage={defaultMessage}
          removeFilter={() => removeFilter("many-fields", { ...tag })}
        >
          {firstFieldContent}
          {separator}
          {secondFieldContent}
          {searchFilters[subfields[2]] && (
            <>&nbsp; &nbsp; {searchFilters[subfields[2]].label}</>
          )}
        </AudienceFilterLabel>
      );
    };

    const createSocialProviderLabel = (tag, index) => {
      const { name, id, defaultMessage, field } = tag;
      const providerData = socialProviders.filter((item) => {
        socialProviderIndex = index;
        return item.value === searchFilters[name].value;
      })[0];

      return (
        <TooltipPortal
          content={
            <IDHFormattedMessage
              id="front_you_can_select_only_one_platform"
              defaultMessage="You can select only one platform"
            />
          }
        >
          <AudienceFilterLabel
            key={`${id}_${-index}`}
            id={id}
            disabled
            defaultMessage={defaultMessage}
            removeFilter={() => removeFilter("single-field", { ...tag })}
          >
            {providerData[field]}
          </AudienceFilterLabel>
        </TooltipPortal>
      );
    };

    const createSingleFieldLabel = (tag, index) => {
      const { name, id, defaultMessage, field } = tag;

      let content = null;
      if (typeof searchFilters[name] === "boolean") {
        content = null;
      } else if (name === "influencerGender") {
        content = genders.filter(
          (item) => item.value === searchFilters[name].value,
        )[0][field];
      } else if (name === "influencerSocialUsername") {
        content = searchFilters[name];
      } else {
        content = searchFilters[name][field];
      }

      return (
        <AudienceFilterLabel
          key={`${id}_${-index}`}
          id={id}
          disabled={name === "influencerSocialProvider"}
          name={name}
          defaultMessage={defaultMessage}
          removeFilter={() => removeFilter("single-field", { ...tag })}
        >
          {content}
        </AudienceFilterLabel>
      );
    };

    const labels = filteredTagNames.map((tag, index) => {
      const { name, subfields } = tag;

      // Handle array filters
      if (Array.isArray(searchFilters[name])) {
        return searchFilters[name].map((subTag, idx) => {
          if (name === "influencerFollowers") {
            return createInfluencerFollowersLabel(tag, subTag, idx);
          }
          return createArrayFilterLabel(tag, subTag, idx);
        });
      }

      // Handle subfields
      if (
        tag.subfields &&
        (searchFilters[subfields[0]] || searchFilters[subfields[1]])
      ) {
        return createSubfieldsLabel(tag, index);
      }

      // Handle social provider special case
      if (name === "influencerSocialProvider") {
        return createSocialProviderLabel(tag, index);
      }

      // Handle single field filters
      if (searchFilters[name]) {
        return createSingleFieldLabel(tag, index);
      }

      return null;
    });

    const labelsWithSocialProvderAtFirstPlace = METHODS.changeArrayElementOrder(
      labels,
      socialProviderIndex,
      0,
    );
    return labelsWithSocialProvderAtFirstPlace;
  };

  const generateLabelsWithSocialUsername = () => {
    let socialProviderIndex = 0;

    const labels = tagNames.map((tag, index) => {
      const { name, id, defaultMessage, field } = tag;

      const isSelectedProviders =
        name === "selectedProviders" &&
        Array.isArray(searchFilters[name]) &&
        searchFilters[name].length > 0;
      const isSocialProvider =
        name === "influencerSocialProvider" && searchFilters[name];
      const isSocialUsername = name === "influencerSocialUsername";

      if (!isSelectedProviders && !isSocialProvider && !isSocialUsername) {
        return null;
      }

      let children;
      if (isSocialProvider) {
        const matchingProvider = socialProviders.find((item) => {
          socialProviderIndex = index;
          return item.value === searchFilters[name].value;
        });
        children = matchingProvider[field];
      }

      if (isSocialUsername) {
        if (Array.isArray(searchFilters[name])) {
          // Multiple usernames — generate separate tags for each
          return searchFilters[name].map((username, idx) => (
            <AudienceFilterLabel
              key={`${id}_${idx}`}
              id={id}
              defaultMessage={defaultMessage}
              removeFilter={() =>
                removeFilter("social-username", {
                  ...tag,
                  usernameIndex: idx,
                  username,
                })
              }
            >
              {username}
            </AudienceFilterLabel>
          ));
        }
        children = searchFilters[name];
      }

      if (isSelectedProviders) {
        return searchFilters[name].map((subTag, idx) =>
          createArrayFilterLabel(tag, subTag, idx),
        );
      }
      return (
        <AudienceFilterLabel
          key={`${id}_${-index}`}
          id={id}
          defaultMessage={defaultMessage}
          disabled={isSocialProvider}
          removeFilter={() => removeFilter("single-field", { ...tag })}
        >
          {children}
        </AudienceFilterLabel>
      );
    });

    const labelsWithSocialProvderAtFirstPlace = METHODS.changeArrayElementOrder(
      labels,
      socialProviderIndex,
      0,
    );
    return labelsWithSocialProvderAtFirstPlace;
  };

  return (
    <div className="audience-search__labels">
      {searchFilters.influencerSocialUsername?.length
        ? generateLabelsWithSocialUsername()
        : generateLabels()}
    </div>
  );
}

export default withRouter(injectIntl(SearchLabels));
